import {format} from 'date-fns';

const PUBLIC_BRAND_NAME='BPA Solutions';
const PUBLIC_BRAND_LONG_NAME='Business Process Automation Solutions';
const LEGAL_ENTITY_NAME='Anish Malhotra Pty Ltd';
const LEGAL_TRADING_STATEMENT=`${PUBLIC_BRAND_NAME} (${PUBLIC_BRAND_LONG_NAME}) is a trading name of ${LEGAL_ENTITY_NAME}.`;
const BLOCK_STYLE_RE=/\s*\{::\s*([^}]*)\}\s*$/;
const LINE_SPACING_OPTIONS=['1','1.15','1.5','2','2.5','3'];

const escapeHtml=(value)=> {
  if (!value) return '';
  return String(value)
    .replace(/&/g,'&amp;')
    .replace(/</g,'&lt;')
    .replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;')
    .replace(/'/g,'&#39;');
};

const escapeAttribute=(value)=> escapeHtml(value);

const sanitizeLinkUrl=(value)=> {
  if (!value) return '#';
  try {
    const protocol=new URL(value,window.location.href).protocol;
    if (['https:','http:','mailto:'].includes(protocol)) return value;
  } catch (error) {
    console.warn('URL parsing error:',error);
  }
  return '#';
};

const sanitizeAssetUrl=(value)=> {
  if (!value) return '';
  try {
    const url=new URL(value,window.location.href);
    return ['https:','http:'].includes(url.protocol) ? url.toString() : '';
  } catch {
    return '';
  }
};

const parseBlockStyle=(rawLine='')=> {
  const match=String(rawLine).match(BLOCK_STYLE_RE);
  const style={};
  if (!match) return {text:String(rawLine),style};
  for (const token of match[1].trim().split(/\s+/)) {
    const [key,value]=token.split('=');
    if (!key || value==null) continue;
    if (key==='font' && ['helvetica','times','courier'].includes(value)) style.font=value;
    if (key==='size' && ['12','14','16','18','20','24','30'].includes(value)) style.size=value;
    if (key==='color' && /^#[0-9a-fA-F]{6}$/.test(value)) style.color=value.toLowerCase();
    if (key==='line' && LINE_SPACING_OPTIONS.includes(value)) style.line=value;
    if ((key==='before' || key==='after') && ['0','1'].includes(value)) style[key]=value;
  }
  return {text:String(rawLine).replace(match[0],'').trimEnd(),style};
};

const blockStyleToCss=(style={})=> {
  const declarations=[];
  if (style.font==='helvetica') declarations.push('font-family:Arial,Helvetica,sans-serif');
  if (style.font==='times') declarations.push("font-family:Georgia,'Times New Roman',serif");
  if (style.font==='courier') declarations.push("font-family:'Courier New',Courier,monospace");
  if (style.size) declarations.push(`font-size:${style.size}px`);
  if (style.color) declarations.push(`color:${style.color}`);
  if (style.line) declarations.push(`line-height:${style.line}`);
  if (style.before==='1') declarations.push('margin-top:12px');
  if (style.before==='0') declarations.push('margin-top:0');
  if (style.after==='1') declarations.push('margin-bottom:12px');
  if (style.after==='0') declarations.push('margin-bottom:0');
  return declarations.join(';');
};

const styleAttr=(style)=> {
  const css=blockStyleToCss(style);
  return css ? ` style="${css}"` : '';
};

const renderInlineMarkdown=(value)=> {
  if (!value) return '';
  const normalized=String(value).replace(/(\d)\\\./g,'$1.');
  let escaped=escapeHtml(normalized);
  escaped=escaped.replace(/`([^`]+)`/g,'<code>$1</code>');
  escaped=escaped.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>');
  escaped=escaped.replace(/\*(.+?)\*/g,'<em>$1</em>');
  escaped=escaped.replace(/\[(.+?)\]\((.+?)\)/g,(match,text,url)=> {
    const safeUrl=sanitizeLinkUrl(url);
    return `<a href="${escapeAttribute(safeUrl)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });
  return escaped;
};

const renderSafeMarkdownBlocks=(markdown)=> {
  if (!markdown || !markdown.trim()) return '<p>No content available.</p>';

  const blocks=[];
  let currentList=null;
  let listItems=[];

  const flushList=()=> {
    if (!currentList || listItems.length===0) return;
    const items=listItems.map(({text,style})=> `<li${styleAttr(style)}>${renderInlineMarkdown(text)}</li>`).join('');
    blocks.push(`<${currentList}>${items}</${currentList}>`);
    currentList=null;
    listItems=[];
  };

  for (const rawLine of markdown.split('\n')) {
    const parsed=parseBlockStyle(rawLine.trim());
    const line=parsed.text.trim();
    const style=parsed.style;
    if (!line) {flushList();continue;}
    if (line.startsWith('### ')) {flushList();blocks.push(`<h3${styleAttr(style)}>${renderInlineMarkdown(line.slice(4))}</h3>`);continue;}
    if (line.startsWith('## ')) {flushList();blocks.push(`<h2${styleAttr(style)}>${renderInlineMarkdown(line.slice(3))}</h2>`);continue;}
    if (line.startsWith('# ')) {flushList();blocks.push(`<h1${styleAttr(style)}>${renderInlineMarkdown(line.slice(2))}</h1>`);continue;}
    if (line==='---') {flushList();blocks.push('<hr />');continue;}
    if (line.startsWith('> ')) {flushList();blocks.push(`<blockquote${styleAttr(style)}>${renderInlineMarkdown(line.slice(2))}</blockquote>`);continue;}
    const bulletMatch=line.match(/^(?:-|•|●|▪)\s+(.+)$/);
    if (bulletMatch) {if (currentList!=='ul') {flushList();currentList='ul';}listItems.push({text:bulletMatch[1],style});continue;}
    const numberMatch=line.match(/^\d+\.\s+(.+)$/);
    if (numberMatch) {if (currentList!=='ol') {flushList();currentList='ol';}listItems.push({text:numberMatch[1],style});continue;}
    flushList();
    blocks.push(`<p${styleAttr(style)}>${renderInlineMarkdown(line)}</p>`);
  }

  flushList();
  return blocks.length ? blocks.join('') : '<p>No content available.</p>';
};

const stripInlineMarkdown=(value='')=> String(value)
  .replace(/(\d)\\\./g,'$1.')
  .replace(/\[([^\]]+)\]\(([^)]+)\)/g,'$1 ($2)')
  .replace(/\*\*(.*?)\*\*/g,'$1')
  .replace(/\*(.*?)\*/g,'$1')
  .replace(/`([^`]+)`/g,'$1')
  .trim();

const parsePdfBlocks=(markdown='')=> {
  const blocks=[];
  for (const rawLine of String(markdown).split('\n')) {
    const parsed=parseBlockStyle(rawLine.trim());
    const line=parsed.text.trim();
    const style=parsed.style;
    if (!line) {blocks.push({type:'space',style});continue;}
    if (line==='---') {blocks.push({type:'rule',style});continue;}
    if (line.startsWith('### ')) {blocks.push({type:'h3',text:stripInlineMarkdown(line.slice(4)),style});continue;}
    if (line.startsWith('## ')) {blocks.push({type:'h2',text:stripInlineMarkdown(line.slice(3)),style});continue;}
    if (line.startsWith('# ')) {blocks.push({type:'h1',text:stripInlineMarkdown(line.slice(2)),style});continue;}
    if (line.startsWith('> ')) {blocks.push({type:'quote',text:stripInlineMarkdown(line.slice(2)),style});continue;}
    const bullet=line.match(/^(?:-|•|●|▪)\s+(.+)$/);
    if (bullet) {blocks.push({type:'bullet',text:stripInlineMarkdown(bullet[1]),style});continue;}
    const numbered=line.match(/^(\d+)\.\s+(.+)$/);
    if (numbered) {blocks.push({type:'number',prefix:`${numbered[1]}.`,text:stripInlineMarkdown(numbered[2]),style});continue;}
    blocks.push({type:'p',text:stripInlineMarkdown(line),style});
  }
  return blocks;
};

const formatDate=(value)=> {
  if (!value) return 'Not specified';
  const parsed=new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not specified' : format(parsed,'dd MMMM yyyy');
};

const safeFilePart=(value='document')=> String(value)
  .trim()
  .replace(/[^a-z0-9]+/gi,'_')
  .replace(/^_+|_+$/g,'')
  .toLowerCase() || 'document';

const blobToDataUrl=(blob)=> new Promise((resolve,reject)=> {
  const reader=new FileReader();
  reader.onload=()=> resolve(String(reader.result || ''));
  reader.onerror=()=> reject(new Error('Unable to read logo image.'));
  reader.readAsDataURL(blob);
});

class ExportService {
  async exportDocument(doc,exportFormat) {
    try {
      if (!doc || !doc.version) throw new Error('Invalid document: missing version data');
      const exportData={
        document:{
          title:doc.title,
          type:doc.type,
          version:doc.currentVersion || doc.version.versionNumber,
          status:doc.version.status,
          effectiveDate:doc.version.effectiveDate,
          publishedDate:doc.version.publishedDate,
          archivedDate:doc.version.archivedDate,
          contentHash:doc.version.contentHash || '',
        },
        brand:doc.brand || {name:PUBLIC_BRAND_NAME,abn:'83 679 369 932'},
        content:doc.version.content,
        exportedDate:new Date().toISOString(),
      };

      switch (String(exportFormat).toUpperCase()) {
        case 'PDF': return await this.exportToPDF(exportData);
        case 'WORD':
        case 'DOC':
        case 'DOCX': return this.exportToWord(exportData);
        case 'PRINT': return this.printDocument(exportData);
        default: throw new Error(`Unsupported format: ${exportFormat}`);
      }
    } catch (error) {
      console.error('Export failed:',error);
      throw new Error(`Export failed: ${error.message}`);
    }
  }

  async loadLogoData(logoUrl) {
    const safeUrl=sanitizeAssetUrl(logoUrl);
    if (!safeUrl) return null;
    try {
      const response=await fetch(safeUrl,{mode:'cors',credentials:'omit'});
      if (!response.ok) return null;
      const blob=await response.blob();
      if (!['image/png','image/jpeg','image/jpg'].includes(blob.type)) return null;
      return await blobToDataUrl(blob);
    } catch (error) {
      console.warn('PDF logo could not be loaded; using text fallback.',error);
      return null;
    }
  }

  async exportToPDF(data) {
    const {jsPDF}=await import('jspdf');
    const pdf=new jsPDF({orientation:'portrait',unit:'mm',format:'a4',compress:true});
    pdf.setProperties({title:`${data.document.title} - ${data.document.version}`,subject:data.document.type || 'BPA Solutions contract or policy',author:PUBLIC_BRAND_NAME,creator:'BPA Solutions Contract & Policy Hub'});
    const logoData=await this.loadLogoData(data.brand.logo);
    this.renderPdf(pdf,data,logoData);
    const filename=`${safeFilePart(data.document.title)}_${safeFilePart(data.document.version)}.pdf`;
    pdf.save(filename);
    return {success:true,message:'PDF downloaded successfully.'};
  }

  renderPdf(pdf,data,logoData) {
    const pageWidth=210;
    const marginX=18;
    const contentWidth=pageWidth-(marginX*2);
    const footerY=286;
    const bottomLimit=273;
    const brand=data.brand || {};
    const abn=String(brand.abn || '83 679 369 932');
    const acn=String(brand.acn || '679 369 932');
    const website=String(brand.website || 'www.bpasolutions.au');
    const email=String(brand.legalEmail || brand.supportEmail || brand.email || 'support@bpasolutions.au');
    const gold=[140,106,47];
    const goldLight=[215,187,121];
    const navy=[15,23,42];
    const ink=[18,18,18];
    const border=[218,222,229];

    const setInk=()=> pdf.setTextColor(...ink);

    const drawLogo=(x,y,maxWidth,maxHeight)=> {
      if (!logoData) {
        pdf.setDrawColor(...gold);
        pdf.setLineWidth(0.45);
        pdf.roundedRect(x,y,37,12,1.5,1.5,'S');
        setInk();
        pdf.setFont('helvetica','bold');
        pdf.setFontSize(9);
        pdf.text('BPA SOLUTIONS',x+18.5,y+7.5,{align:'center'});
        return;
      }
      try {
        const properties=pdf.getImageProperties(logoData);
        const ratio=Math.min(maxWidth/properties.width,maxHeight/properties.height);
        pdf.addImage(logoData,properties.fileType || 'PNG',x,y,properties.width*ratio,properties.height*ratio,undefined,'FAST');
      } catch (error) {
        console.warn('PDF logo rendering failed; using text fallback.',error);
        setInk();
        pdf.setFont('helvetica','bold');
        pdf.setFontSize(10);
        pdf.text('BPA SOLUTIONS',x,y+7);
      }
    };

    const drawBrandRule=()=> {
      pdf.setFillColor(...navy);
      pdf.rect(marginX,34,contentWidth,0.45,'F');
      pdf.setFillColor(...gold);
      pdf.rect(marginX,34,52,0.9,'F');
    };

    const drawFirstPageHeader=()=> {
      drawLogo(marginX,12,46,17);
      setInk();
      pdf.setFont('helvetica','bold');
      pdf.setFontSize(9.6);
      pdf.text(PUBLIC_BRAND_NAME,pageWidth-marginX,14,{align:'right'});
      pdf.setFont('helvetica','normal');
      pdf.setFontSize(7.1);
      pdf.text(PUBLIC_BRAND_LONG_NAME,pageWidth-marginX,18.8,{align:'right'});
      pdf.text(`Trading name of ${LEGAL_ENTITY_NAME}`,pageWidth-marginX,23,{align:'right'});
      pdf.text(`ABN ${abn}${acn ? `  |  ACN ${acn}` : ''}`,pageWidth-marginX,27.2,{align:'right'});
      drawBrandRule();
    };

    const drawContinuationHeader=()=> {
      setInk();
      pdf.setFont('helvetica','bold');
      pdf.setFontSize(7.6);
      pdf.text('BPA SOLUTIONS',marginX,13.7);
      pdf.setFont('helvetica','normal');
      pdf.setFontSize(7.1);
      const headerText=`${data.document.title} | ${data.document.version}`;
      const headerLines=pdf.splitTextToSize(headerText,105);
      pdf.text(headerLines.slice(0,1),pageWidth-marginX,13.7,{align:'right'});
      pdf.setDrawColor(...border);
      pdf.setLineWidth(0.3);
      pdf.line(marginX,19.5,pageWidth-marginX,19.5);
      pdf.setDrawColor(...goldLight);
      pdf.setLineWidth(0.65);
      pdf.line(marginX,19.5,marginX+34,19.5);
    };

    const addFooter=(pageNumber,totalPages)=> {
      pdf.setDrawColor(...border);
      pdf.setLineWidth(0.3);
      pdf.line(marginX,footerY-5,pageWidth-marginX,footerY-5);
      setInk();
      pdf.setFont('helvetica','normal');
      pdf.setFontSize(6.6);
      const leftFooter=`${PUBLIC_BRAND_NAME} | ${data.document.title} | ${data.document.version}`;
      const leftLines=pdf.splitTextToSize(leftFooter,125);
      pdf.text(leftLines.slice(0,1),marginX,footerY);
      pdf.text(`Page ${pageNumber} of ${totalPages}`,pageWidth-marginX,footerY,{align:'right'});
      pdf.setFontSize(6.1);
      pdf.text(LEGAL_TRADING_STATEMENT,marginX,footerY+4);
    };

    drawFirstPageHeader();
    let y=47;

    setInk();
    pdf.setFont('helvetica','bold');
    pdf.setFontSize(19);
    const titleLines=pdf.splitTextToSize(String(data.document.title || ''),contentWidth-18);
    pdf.text(titleLines,marginX,y);
    y+=titleLines.length*8;

    pdf.setFont('helvetica','normal');
    pdf.setFontSize(8);
    setInk();
    pdf.text(String(data.document.type || 'Published document').toUpperCase(),marginX,y+1.5);
    y+=8;

    pdf.setDrawColor(...goldLight);
    pdf.setLineWidth(0.45);
    pdf.line(marginX,y,pageWidth-marginX,y);
    y+=5;

    const metaColumns=[
      {label:'Version',value:data.document.version || 'Current'},
      {label:'Effective date',value:formatDate(data.document.effectiveDate)},
      {label:'Published',value:formatDate(data.document.publishedDate)},
      {label:'Status',value:String(data.document.status || 'Published')},
    ];
    const metaWidth=contentWidth/metaColumns.length;
    metaColumns.forEach((item,index)=> {
      const x=marginX+(index*metaWidth);
      if (index>0) {
        pdf.setDrawColor(...border);
        pdf.setLineWidth(0.25);
        pdf.line(x,y-1,x,y+16);
      }
      setInk();
      pdf.setFont('helvetica','bold');
      pdf.setFontSize(6.3);
      pdf.text(item.label.toUpperCase(),x+3,y+3.5);
      pdf.setFont('helvetica','normal');
      pdf.setFontSize(8.2);
      const valueLines=pdf.splitTextToSize(String(item.value),metaWidth-6);
      pdf.text(valueLines.slice(0,2),x+3,y+9.5);
    });
    y+=20;

    pdf.setDrawColor(...border);
    pdf.setLineWidth(0.3);
    pdf.line(marginX,y,pageWidth-marginX,y);
    y+=5;

    setInk();
    pdf.setFont('helvetica','normal');
    pdf.setFontSize(7);
    const identityLines=pdf.splitTextToSize(`${LEGAL_TRADING_STATEMENT} ${website} | ${email}`,contentWidth-2);
    pdf.text(identityLines,marginX,y);
    y+=identityLines.length*3.8+6;

    const addPage=()=> {
      pdf.addPage();
      drawContinuationHeader();
      y=29;
    };

    const ensureSpace=(height)=> {
      if (y+height>bottomLimit) {
        addPage();
        return true;
      }
      return false;
    };

    const applyPdfTextStyle=(options)=> {
      pdf.setFont(options.font || 'helvetica',options.bold ? 'bold' : (options.italic ? 'italic' : 'normal'));
      pdf.setFontSize(options.fontSize || 9.4);
      setInk();
    };

    const writeWrapped=(text,options={})=> {
      const fontSize=options.fontSize || 9.4;
      const lineHeight=options.lineHeight || Math.max(5.1,fontSize*0.55);
      const indent=options.indent || 0;
      const prefix=options.prefix || '';
      const prefixSpace=prefix ? 7 : 0;
      const maxWidth=Math.max(20,contentWidth-indent-prefixSpace-1.5);
      const before=options.before || 0;
      const after=options.after ?? 2.4;

      applyPdfTextStyle({...options,fontSize});
      const lines=pdf.splitTextToSize(String(text || ''),maxWidth);

      if (options.keepTogether) {
        const keepLines=Math.min(lines.length,options.keepLines || 2);
        if (ensureSpace(before+(keepLines*lineHeight)+after)) applyPdfTextStyle({...options,fontSize});
      }
      if (before>0) {
        if (ensureSpace(before)) applyPdfTextStyle({...options,fontSize});
        y+=before;
      }

      let index=0;
      let firstChunk=true;
      while (index<lines.length) {
        applyPdfTextStyle({...options,fontSize});
        let availableLines=Math.floor((bottomLimit-y)/lineHeight);
        const remaining=lines.length-index;

        if (availableLines<1 || (firstChunk && remaining>1 && availableLines===1)) {
          addPage();
          applyPdfTextStyle({...options,fontSize});
          availableLines=Math.floor((bottomLimit-y)/lineHeight);
        }

        let take=Math.min(remaining,Math.max(1,availableLines));
        if (remaining>take && remaining-take===1 && take>1) take-=1;

        if (prefix && firstChunk) {
          pdf.text(prefix,marginX+indent,y);
        }

        const textX=marginX+indent+prefixSpace;
        for (let offset=0;offset<take;offset+=1) {
          pdf.text(lines[index+offset],textX,y);
          y+=lineHeight;
        }
        index+=take;
        firstChunk=false;

        if (index<lines.length) {
          addPage();
          applyPdfTextStyle({...options,fontSize});
        }
      }

      if (after>0 && y+after<=bottomLimit) y+=after;
    };

    const withCustomStyle=(base,style={})=> {
      const customFontSize=style.size ? Number(style.size)*0.75 : base.fontSize;
      const baseLineHeight=style.size
        ? Math.max(base.lineHeight || 0,customFontSize*0.56)
        : (base.lineHeight || Math.max(5.1,customFontSize*0.56));
      const lineMultiplier=style.line ? Number(style.line) : 1;
      return {
        ...base,
        font:style.font || base.font,
        fontSize:customFontSize,
        lineHeight:baseLineHeight*lineMultiplier,
        before:style.before==='1' ? 3 : (style.before==='0' ? 0 : base.before),
        after:style.after==='1' ? 5.5 : (style.after==='0' ? 0 : base.after),
        color:ink,
      };
    };

    const blocks=parsePdfBlocks(data.content);
    for (const block of blocks) {
      if (block.type==='space') {
        if (y+2<=bottomLimit) y+=2;
        continue;
      }
      if (block.type==='rule') {
        ensureSpace(8);
        pdf.setDrawColor(...goldLight);
        pdf.setLineWidth(0.35);
        pdf.line(marginX,y+2,pageWidth-marginX,y+2);
        y+=8;
        continue;
      }
      if (block.type==='h1') {
        ensureSpace(22);
        y+=2;
        writeWrapped(block.text,withCustomStyle({fontSize:12.8,bold:true,lineHeight:6.5,after:2.2,keepTogether:true,keepLines:2},block.style));
        pdf.setDrawColor(...border);
        pdf.setLineWidth(0.25);
        pdf.line(marginX,y,pageWidth-marginX,y);
        y+=3.2;
        continue;
      }
      if (block.type==='h2') {
        ensureSpace(20);
        y+=2;
        writeWrapped(block.text,withCustomStyle({fontSize:11.4,bold:true,lineHeight:6,after:1.5,keepTogether:true,keepLines:2},block.style));
        pdf.setDrawColor(...goldLight);
        pdf.setLineWidth(0.4);
        pdf.line(marginX,y,pageWidth-marginX,y);
        y+=3.2;
        continue;
      }
      if (block.type==='h3') {
        ensureSpace(17);
        y+=1.5;
        writeWrapped(block.text,withCustomStyle({fontSize:10.4,bold:true,lineHeight:5.7,after:2.2,keepTogether:true,keepLines:2},block.style));
        continue;
      }
      if (block.type==='quote') {
        ensureSpace(12);
        pdf.setDrawColor(...goldLight);
        pdf.setLineWidth(0.8);
        pdf.line(marginX,y-1,marginX,y+9);
        writeWrapped(block.text,withCustomStyle({fontSize:9.3,italic:true,lineHeight:5.25,indent:4,after:4},block.style));
        continue;
      }
      if (block.type==='bullet') {
        writeWrapped(block.text,withCustomStyle({fontSize:9.35,lineHeight:5.25,indent:2,prefix:'•',after:1.8},block.style));
        continue;
      }
      if (block.type==='number') {
        writeWrapped(block.text,withCustomStyle({fontSize:9.35,lineHeight:5.25,indent:1,prefix:block.prefix,after:1.8},block.style));
        continue;
      }
      writeWrapped(block.text,withCustomStyle({fontSize:9.35,lineHeight:5.25,after:2.8},block.style));
    }

    const totalPages=pdf.getNumberOfPages();
    for (let page=1;page<=totalPages;page+=1) {
      pdf.setPage(page);
      addFooter(page,totalPages);
    }
  }

  exportToWord(data) {
    const wordContent=this.generateWordContent(data);
    const blob=new Blob([wordContent],{type:'application/msword'});
    const url=URL.createObjectURL(blob);
    const anchor=document.createElement('a');
    anchor.href=url;anchor.download=`${safeFilePart(data.document.title)}_${safeFilePart(data.document.version)}.doc`;document.body.appendChild(anchor);anchor.click();document.body.removeChild(anchor);URL.revokeObjectURL(url);
    return {success:true,message:'Internal Word export completed.'};
  }

  printDocument(data) {
    const printWindow=window.open('','_blank','width=900,height=700');
    if (!printWindow) throw new Error('Popup blocked. Please allow popups for this site.');
    printWindow.document.write(this.generatePrintableHTML(data));printWindow.document.close();setTimeout(()=> printWindow.print(),500);
    return {success:true,message:'Print dialog opened.'};
  }

  generatePremiumHtml(data,{wordMode=false}={}) {
    const {document:documentData,brand,content}=data;
    const htmlContent=renderSafeMarkdownBlocks(content);
    const safeTitle=escapeHtml(documentData.title);
    const safeType=escapeHtml(documentData.type || 'Published document');
    const safeVersion=escapeHtml(documentData.version || 'Current');
    const safeStatus=escapeHtml(documentData.status || 'Published');
    const safeAbn=escapeHtml(brand.abn || '83 679 369 932');
    const safeAcn=escapeHtml(brand.acn || '679 369 932');
    const safeWebsite=escapeHtml(brand.website || 'www.bpasolutions.au');
    const safeEmail=escapeHtml(brand.legalEmail || brand.supportEmail || brand.email || 'support@bpasolutions.au');
    const safeLogo=sanitizeAssetUrl(brand.logo || '');
    const safeDisclaimer=escapeHtml(brand.footerDisclaimer || 'This document is maintained and published by BPA Solutions.');
    const pageMargin=wordMode ? '0.7in 0.75in 0.75in' : '14mm 16mm 16mm';

    return `<!DOCTYPE html>
<html${wordMode ? ' xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"' : ''}>
<head>
  <meta charset="utf-8">
  <title>${safeTitle} - ${safeVersion}</title>
  ${wordMode ? '<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>90</w:Zoom></w:WordDocument></xml><![endif]-->' : ''}
  <style>
    @page { size: A4; margin: ${pageMargin}; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #263244; font-size: 10.5pt; line-height: 1.55; }
    .sheet { max-width: 760px; margin: 0 auto; }
    .letterhead { width: 100%; border-collapse: collapse; margin: 0 0 10px; }
    .letterhead td { vertical-align: top; padding: 0; }
    .logo-cell { width: 44%; text-align: left; }
    .identity-cell { width: 56%; text-align: right; color: #475569; font-size: 8pt; line-height: 1.45; }
    .logo-img { display: block; max-width: 155px; max-height: 48px; width: auto; height: auto; }
    .logo-fallback { display: inline-block; padding: 9px 15px; border: 1px solid #8c6a2f; color: #0f172a; font-weight: 700; letter-spacing: .06em; font-size: 10pt; }
    .brand { color: #0f172a; font-size: 10pt; font-weight: 700; margin-bottom: 2px; }
    .gold-rule { border: 0; border-top: 2px solid #b38a45; margin: 8px 0 24px; }
    .document-kicker { color: #8c6a2f; font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 6px; }
    .document-title { color: #0f172a; font-size: 23pt; line-height: 1.15; font-weight: 700; margin: 0 0 16px; }
    .meta { width: 100%; border-collapse: collapse; margin: 0 0 26px; background: #f8fafc; border: 1px solid #e2e8f0; }
    .meta td { width: 25%; padding: 10px 11px; border-right: 1px solid #e2e8f0; vertical-align: top; }
    .meta td:last-child { border-right: 0; }
    .meta-label { display: block; color: #94a3b8; font-size: 7pt; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px; }
    .meta-value { color: #0f172a; font-size: 9pt; font-weight: 600; }
    .content { color: #334155; }
    .content h1 { color: #0f172a; font-size: 17pt; line-height: 1.25; margin: 25px 0 10px; page-break-after: avoid; }
    .content h2 { color: #0f172a; font-size: 14pt; line-height: 1.3; margin: 23px 0 10px; padding-bottom: 5px; border-bottom: 1px solid #d7bb79; page-break-after: avoid; }
    .content h3 { color: #0f172a; font-size: 11.5pt; margin: 18px 0 7px; page-break-after: avoid; }
    .content p { margin: 0 0 10px; }
    .content ul, .content ol { margin: 8px 0 13px; padding-left: 24px; }
    .content li { margin: 0 0 6px; }
    .content blockquote { margin: 12px 0; padding: 8px 12px; border-left: 3px solid #b38a45; background: #fbf8f1; }
    .content hr { border: 0; border-top: 1px solid #e2e8f0; margin: 20px 0; }
    .content a { color: #705124; }
    .content code { font-family: Consolas, monospace; background: #f1f5f9; padding: 1px 3px; }
    .footer { margin-top: 34px; padding-top: 12px; border-top: 1px solid #d9dee7; color: #64748b; font-size: 7.5pt; }
    .footer-table { width: 100%; border-collapse: collapse; }
    .footer-table td { padding: 0; vertical-align: top; }
    .footer-right { text-align: right; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="sheet">
    <table class="letterhead" role="presentation"><tr><td class="logo-cell">${safeLogo ? `<img src="${escapeAttribute(safeLogo)}" alt="BPA Solutions" class="logo-img" />` : '<div class="logo-fallback">BPA SOLUTIONS</div>'}</td><td class="identity-cell"><div class="brand">BPA Solutions</div><div>Business Process Automation Solutions</div><div>Trading name of Anish Malhotra Pty Ltd</div><div>ABN ${safeAbn}${safeAcn ? ` | ACN ${safeAcn}` : ''}</div><div>${safeWebsite} | ${safeEmail}</div></td></tr></table>
    <hr class="gold-rule" />
    <div class="document-kicker">${safeType}</div>
    <h1 class="document-title">${safeTitle}</h1>
    <table class="meta" role="presentation"><tr><td><span class="meta-label">Version</span><span class="meta-value">${safeVersion}</span></td><td><span class="meta-label">Effective date</span><span class="meta-value">${escapeHtml(formatDate(documentData.effectiveDate))}</span></td><td><span class="meta-label">Published</span><span class="meta-value">${escapeHtml(formatDate(documentData.publishedDate))}</span></td><td><span class="meta-label">Status</span><span class="meta-value">${safeStatus}</span></td></tr></table>
    <div class="content">${htmlContent}</div>
    <div class="footer"><table class="footer-table" role="presentation"><tr><td>${safeDisclaimer}<br />${escapeHtml(LEGAL_TRADING_STATEMENT)}</td><td class="footer-right">${safeTitle}<br />${safeVersion}</td></tr></table></div>
  </div>
</body>
</html>`;
  }

  generatePrintableHTML(data) {return this.generatePremiumHtml(data,{wordMode:false});}
  generateWordContent(data) {return this.generatePremiumHtml(data,{wordMode:true});}
}

export const exportService=new ExportService();